import { buttonVariants, cn, Container, Section } from '@resolute/ui';
import { ChevronDown } from 'lucide-react';
import { useId, useState } from 'react';
import { Link } from 'react-router-dom';

import { Reveal } from '@/components/motion/reveal';
import { PageHero } from '@/components/page-hero';
import { Seo } from '@/lib/seo';

interface Faq {
  question: string;
  answer: string;
}

/**
 * A controlled disclosure rather than native `<details>/<summary>` — native
 * details snaps its content open/shut instantly (no height to animate), which
 * read as a jarring mismatch against the chevron icon smoothly rotating right
 * next to it. `grid-template-rows: 0fr → 1fr` is a CSS-only technique that
 * transitions smoothly without JS measuring `scrollHeight`.
 */
function FaqItem({ faq }: { faq: Faq }) {
  const [open, setOpen] = useState(false);
  const panelId = useId();

  return (
    <div
      className={cn(
        'rounded-lg border border-border bg-card transition-shadow duration-base ease-out-expo',
        open && 'shadow-sm',
      )}
    >
      <button
        type="button"
        aria-expanded={open}
        aria-controls={panelId}
        onClick={() => setOpen((v) => !v)}
        className="flex w-full items-center justify-between gap-4 p-5 text-left font-medium"
      >
        {faq.question}
        <ChevronDown
          className={cn(
            'size-4 shrink-0 text-muted-foreground transition-transform duration-200 ease-out-expo',
            open && 'rotate-180',
          )}
          aria-hidden="true"
        />
      </button>
      <div
        id={panelId}
        className="grid transition-[grid-template-rows] duration-base ease-out-expo"
        style={{ gridTemplateRows: open ? '1fr' : '0fr' }}
      >
        <div className="overflow-hidden">
          <p className="px-5 pb-5 text-muted-foreground">{faq.answer}</p>
        </div>
      </div>
    </div>
  );
}

const CATEGORIES: { heading: string; items: Faq[] }[] = [
  {
    heading: 'Getting started',
    items: [
      {
        question: 'Do I need an account to browse or enquire about a property?',
        answer:
          'No. Searching, filtering, saving to your Wishlist, comparing properties and sending an enquiry all work without creating an account or logging in — everything is tied to your browser, not a login.',
      },
      {
        question: 'How do I search for a property?',
        answer:
          'Use the search bar on the homepage (by project, society, builder or locality) or the filter row above it (city, type, BHK, status, budget). You can also browse by category — For sale, For rent, Commercial, GIFT City and more — from the Inventory menu.',
      },
    ],
  },
  {
    heading: 'Trust & verification',
    items: [
      {
        question: 'What does "RERA-verified" mean on a listing?',
        answer:
          'Every listing carries its RERA registration number and supporting documents before it’s published on the site — it’s checked upfront, not something you have to ask a broker to produce.',
      },
      {
        question: 'How is my personal information used?',
        answer:
          'See our Privacy Policy for the full breakdown of what we collect and why. In short: your enquiry details go only to us and, where relevant, the specific developer of the property you asked about — never sold or shared for third-party marketing.',
      },
    ],
  },
  {
    heading: 'Buying, renting & site visits',
    items: [
      {
        question: 'How do I schedule a site visit?',
        answer:
          'On any property page (or the inquiry popup on a property card), turn on "Schedule a site visit" in the enquiry form and pick a date and time. It’s submitted alongside your contact details in one step.',
      },
      {
        question: 'How quickly will someone respond to my enquiry?',
        answer: 'An advisor will be in touch within one business day of you submitting an enquiry.',
      },
      {
        question: 'Can I save or compare properties without registering?',
        answer:
          'Yes — your Wishlist and Compare list (up to 4 properties at once) both work straight away, no account needed.',
      },
    ],
  },
  {
    heading: 'Investment & NRI buyers',
    items: [
      {
        question: 'What is GIFT City?',
        answer:
          'GIFT City (Gujarat International Finance Tec-City) is India’s international financial services SEZ. We curate residential and commercial properties there for buyers and investors specifically interested in that market.',
      },
      {
        question: 'Do you have tools to estimate EMI, affordability or rental returns?',
        answer:
          'Yes — the EMI Calculator, Affordability Calculator and Rental Yield Calculator are all free to use from the Tools menu, no sign-up required.',
      },
      {
        question: 'I’m an NRI — can I complete due diligence without visiting in person?',
        answer:
          'Yes. You get the same verification documents and advisor support as someone walking the site in person — enquire and we’ll coordinate everything remotely.',
      },
    ],
  },
  {
    heading: 'Pricing',
    items: [
      {
        question: 'Is the price shown on a listing final?',
        answer:
          'The price shown is an indicative guide price. Final pricing, taxes and terms are confirmed directly with the developer or your advisor before you commit to anything.',
      },
    ],
  },
];

const faqJsonLd = {
  '@context': 'https://schema.org',
  '@type': 'FAQPage',
  mainEntity: CATEGORIES.flatMap((category) =>
    category.items.map((faq) => ({
      '@type': 'Question',
      name: faq.question,
      acceptedAnswer: { '@type': 'Answer', text: faq.answer },
    })),
  ),
};

export default function FaqPage() {
  return (
    <>
      <Seo
        title="Frequently Asked Questions"
        description="Answers to common questions about searching, verification, site visits, NRI buying and more — plus how to reach us if you still need help."
        path="/faq"
        jsonLd={faqJsonLd}
      />

      <PageHero
        eyebrow="Support"
        title="Frequently Asked Questions."
        titleLine2="Answers, upfront."
        description="Answers to what buyers and renters ask us most. Still stuck? Get in touch with us at the bottom of this page."
      />

      <Section>
        <Container className="max-w-3xl">
          {/* Each category reveals independently as it's scrolled to — not a
              manual index-proportional delay, which only "staggers" the first
              screenful (categories further down the page would already be
              fully revealed by the time they're actually scrolled into view,
              since Reveal fires on ITS OWN viewport entry). */}
          <div className="space-y-10">
            {CATEGORIES.map((cat) => (
              <Reveal key={cat.heading}>
                <h2 className="text-lg font-semibold">{cat.heading}</h2>
                <div className="mt-4 space-y-3">
                  {cat.items.map((faq) => (
                    <FaqItem key={faq.question} faq={faq} />
                  ))}
                </div>
              </Reveal>
            ))}
          </div>
        </Container>
      </Section>

      <Section spacing="lg" className="border-t border-border bg-muted/30">
        <Container className="max-w-3xl">
          <Reveal className="text-center">
            <h2 className="font-display text-2xl font-semibold tracking-tight">
              Still have a question?
            </h2>
            <p className="mx-auto mt-2 max-w-xl text-muted-foreground">
              Reach our team directly and an advisor will get back to you within one business day.
            </p>
            <Link to="/contact" className={cn(buttonVariants({ variant: 'primary', size: 'lg' }), 'mt-6')}>
              Contact us
            </Link>
          </Reveal>
        </Container>
      </Section>
    </>
  );
}
